import { useState } from 'react';
import { ChevronRight, GraduationCap, BookOpen, CheckCircle, FileText, HelpCircle, Compass, Clock, Users, Lightbulb, Search, Award, Globe, FileCheck, Building, Mail } from 'lucide-react';
import HighlightText from '../components/core/HomePage/HighlightText';
import Footer from '../components/common/Footer';

export default function DBAGuidancePage() {
  const [activeTab, setActiveTab] = useState('overview');

  const specializationAreas = [
    { category: "Core Business Specializations", fields: [
      "Strategic Leadership", "Marketing Management", "Finance and Accounting", "Entrepreneurship",
      "Organizational Behavior", "Global Business", "Supply Chain Management", "Information Technology Management",
      "Healthcare Management", "Human Resource Management"
    ]},
    { category: "Emerging Business Fields", fields: [
      "Sustainable Business", "Corporate Governance", "Innovation and Technology Management", "International Business",
      "Strategic Management", "Project Management", "Marketing Analytics", "Risk Management",
      "Leadership Development", "Business Ethics and Corporate Social Responsibility"
    ]}
  ];

  const recognitions = [
    { name: "French Ministry of National Education", description: "Officially listed as an educational institution" },
    { name: "Florida Department of Education (USA)", description: "Annual Verification as a Religious College" },
    { name: "Ministry of Education, Culture & Higher Education", description: "Authorization and Recognition as a Degree-granting Higher Education Institution" },
    { name: "Ministry of Education & Higher Education", description: "Authorization and Recognition as a Degree-granting Higher Education Institution" },
    { name: "European Council of Leading Business Schools", description: "Registered and approved member institution" },
    { name: "International Association for Quality Assurance in Higher Education", description: "Full accreditation as a degree-granting institution" },
    { name: "United Nations Department of Economic and Social Affairs", description: "Listed in the United Nations Department of Economic and Social Affairs database" },
    { name: "Office of The Minister of Education, African Diaspora", description: "Accreditation and Recognition as a Degree-granting Higher Education Institution" },
    { name: "Quality Education Assurance Accreditation", description: "Full accreditation as a degree-granting institution" }
  ];

  const admissionRequirements = [
    { title: "Academic Background", description: "Master's degree from a recognizable institution with a strong academic record and research capabilities." },
    { title: "Professional Experience", description: "Demonstrated professional experience in business or related areas, typically 5+ years of management experience." },
    { title: "Research Vision", description: "Clear research interests and career goals outlined in a statement of purpose." },
    { title: "Documentation", description: "Complete CV or resume documenting professional and academic achievements." },
    { title: "Language Proficiency", description: "English language proficiency test scores (if applicable)." },
    { title: "Application Review", description: "Holistic evaluation considering both academic achievements and professional experience." }
  ];

  return (
    <div className="min-h-screen bg-richblue-5">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Doctor of Business Administration (DBA)</h1>
          <p className="text-xl text-richblue-500">Advance your business expertise with our research-based doctoral program</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-richblue-50 border-b border-richblue-300 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto py-2 space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'overview' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specializations')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'specializations' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Specializations
            </button>
            <button 
              onClick={() => setActiveTab('admissions')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'admissions' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Requirements
            </button>
            <button 
              onClick={() => setActiveTab('recognition')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'recognition' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Recognition
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Overview Section */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-richblue-700 mb-4">Program Overview</h2>
                <p className="text-lg text-richblue-500 mb-4">
                  The Doctor of Business Administration (DBA) at Kennedy University is designed for professionals 
                  seeking to advance their expertise in business leadership, strategy, and research.
                </p>
                <p className="text-lg text-richblue-500 mb-4">
                  This program provides rigorous academic training and emphasizes significant research contributions 
                  that advance knowledge in the business field.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <BookOpen className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Research-Focused</h3>
                  <p className="text-richblue-500">
                    Our DBA program emphasizes significant research contributions that advance business knowledge.
                  </p>
                </div>
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <Globe className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Global Access</h3>
                  <p className="text-richblue-500">
                    Complete your doctorate from anywhere in the world with our flexible online programs.
                  </p>
                </div>
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <GraduationCap className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Professional Growth</h3>
                  <p className="text-richblue-500">
                    Advance your career with advanced business knowledge and research skills.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                <h3 className="text-xl font-semibold mb-4 text-richblue-600">Program Structure</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-richblue-700">Phase 1: Coursework</h4>
                    <ul className="mt-2 text-richblue-500 space-y-2">
                      <li className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span>Advanced research methodologies</span>
                      </li>
                      <li className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span>Specialized business knowledge</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-richblue-700">Phase 2: Research</h4>
                    <ul className="mt-2 text-richblue-500 space-y-2">
                      <li className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span>Research proposal development</span>
                      </li>
                      <li className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span>Dissertation writing and defense</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Specializations Section */}
        {activeTab === 'specializations' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Areas of Specialization</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {specializationAreas.map((area, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <h3 className="text-xl font-semibold text-richblue-600 mb-4">{area.category}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {area.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex} className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span className="text-richblue-500 text-sm">{field}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admission Requirements Section */}
        {activeTab === 'admissions' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Admission Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {admissionRequirements.map((req, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <h3 className="text-xl font-semibold text-richblue-600 mb-4">{req.title}</h3>
                  <p className="text-richblue-500">{req.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Accreditations Section */}
        {activeTab === 'recognition' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Institutional Recognitions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recognitions.map((recognition, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="mb-4">
                    <div className="bg-richblue-100 p-3 rounded-full w-fit">
                      <Award className="h-8 w-8 text-richblue-400" />
                    </div>
                  </div>
                  <h3 className="font-medium text-richblue-700 mb-2">{recognition.name}</h3>
                  <p className="text-richblue-500 text-sm">{recognition.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}